import {
  Component,
  ElementRef,
  inject,
  input,
  InputSignal,
  Signal,
  viewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { JobService } from '../../services/job.service';

@Component({
  selector: 'app-audio-line',
  imports: [],
  template: `
    <div class="waveform">
      <canvas #canvas> </canvas>
    </div>
  `,
  styleUrl: './audio-line.component.scss',
})
export class AudioLineComponent {
  private readonly store: Store = inject(Store);
  readonly audio: InputSignal<Blob> = input.required<Blob>();
  canvasRef: Signal<ElementRef<HTMLCanvasElement>> = viewChild.required(
    'canvas',
    { read: ElementRef<HTMLCanvasElement> },
  );

  async ngAfterViewInit(): Promise<void> {
    const audioCtx = new AudioContext();
    const arrayBuffer = await this.audio().arrayBuffer();
    const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
    this.drawWaveform(audioBuffer);
    await audioCtx.close();
  }

  private drawWaveform(audioBuffer: AudioBuffer): void {
    const canvas: HTMLCanvasElement = this.canvasRef().nativeElement;
    const ctx: CanvasRenderingContext2D = canvas.getContext('2d')!;
    const samples: Float32Array<ArrayBuffer> = audioBuffer.getChannelData(0);
    const width: number = canvas.offsetWidth;
    const height: number = canvas.offsetHeight;

    canvas.width = width;
    canvas.height = height;

    const step: number = Math.floor(samples.length / width);
    const amp: number = height / 2;

    ctx.clearRect(0, 0, width, height);
    ctx.beginPath();
    ctx.moveTo(0, amp);

    const gain = 10;
    const points: { x: number; y: number }[] = [];
    for (let x = 0; x < width; x++) {
      const sample = Math.max(-1, Math.min(1, (samples[x * step] ?? 0) * gain));
      points.push({ x, y: amp - sample * amp });
    }

    for (let i = 1; i < points.length - 1; i++) {
      const midX = (points[i].x + points[i + 1].x) / 2;
      const midY = (points[i].y + points[i + 1].y) / 2;
      ctx.quadraticCurveTo(points[i].x, points[i].y, midX, midY);
    }
    ctx.lineTo(points[points.length - 1].x, points[points.length - 1].y);

    ctx.strokeStyle = '#3E7A71';
    ctx.lineWidth = 2;
    ctx.stroke();
  }
}
